import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../General/useSeverityAsPriority";
import { useMemo, useContext, type ReactNode } from "react";
import { GeneralAppContext } from "../../General/GeneralAppProvider";
import type { GeneralAppContextType } from "../../types/GeneralAppProvider";
import {
  getAdditionalDataTitle,
  getKnownAdditionalDataTitle,
} from "../../Findings/FindingDetailsPane/FindingsDetailsPaneComponents/Details/DetailsUtils";
import useLogger, { LOG_SEVERITY } from "../../Logger/useLogger";
import {
  ageFields,
  conditionDateLabel,
  conditionGroupsEnum,
  conditionLabels,
  conditionsEnum,
  conditionTypes,
  deprecatedConditions,
  fieldsToConvertToBeautifulString,
  filterFieldsEnum,
  getActualStatusNodes,
  getConditionGroup,
  getFieldValueOptions,
  getIsDateField,
  isSetGroup,
} from "../../Findings/FindingsFilters/FilterRow/FilterRowUtils";
import { readOnlyFields } from "../../Findings/FindingsFilters/FilterUtils";
import {
  isActualStatusKey,
  isSameRemediationItemFilterKey,
  splitFilterKey,
} from "../FilterManagement/filterKey";
import useAnalystInstanceNames from "../../AnalystRuns/useAnalystInstanceNames";
import {
  convertToBeautifulString,
  dateFormatType,
  getFormattedDate,
} from "@SeemplicityDev/shared";
import { capitalize } from "lodash-es";
import type {
  FieldData,
  FilterItem,
  FilterPinItem,
  FilterValue,
} from "../FilterManagement/CommonFilterTypes";

const MAX_VALUES_LENGTH = 2;

// A filter value at runtime can be a plain string, an array of strings, or a
// legacy/saved-filter object carrying { value, unit } (e.g. date conditions).
type FilterValueLike = FilterItem["value"] | undefined;

// Option shape produced by getFieldValueOptions / getActualStatusNodes, and
// consumed when building selected-value labels.
interface FieldValueOption {
  label?: string;
  value?: unknown;
  [key: string]: unknown;
}

interface ConditionOption {
  label?: string;
  longLabel?: string;
  value: string;
  type?: string;
  deprecated?: boolean;
}

interface SelectedValueData {
  selectedOptions: FieldValueOption[];
  label: ReactNode;
  title: ReactNode;
}

interface UseFilterDetailsReturn {
  fieldData: Partial<FieldData> & { label?: string; pinned?: boolean };
  conditionData: ConditionOption | undefined;
  selectedValueData: SelectedValueData;
  conditionOptions: ConditionOption[];
}

const getFieldLabel = (
  value: FilterValueLike,
  currentValueObj: FieldValueOption[] | undefined,
  field: string | null | undefined,
  condition: ConditionOption | undefined,
  isTitle = false,
  isDateField = false
): ReactNode => {
  // Normalize a possibly-null condition value to a non-matching empty string so
  // the string-keyed group lookup stays well-typed without changing which
  // branch runs at runtime (undefined never resolved to a group).
  const group = getConditionGroup(condition?.value ?? "");
  let label: unknown = "";
  if (!value) {
    label = "Select a Value";
  } else if (splitFilterKey(field).property === filterFieldsEnum.ID) {
    label = `${(value as string[])?.length} selected`;
  } else if (isActualStatusKey(field)) {
    label = currentValueObj?.map((option) => option.label)?.join(", ");
  } else if (
    isDateField &&
    (condition?.value === conditionsEnum.GT ||
      condition?.value === conditionsEnum.LT)
  ) {
    if (
      typeof value === "string" ||
      (value as FilterValue)?.unit === "absolute"
    ) {
      // for backward compatibility
      let date = (value as FilterValue)?.value || value;
      // In this branch `date` is a string date at runtime; narrow the broader
      // FilterValueLike union to the string the formatter expects.
      label = getFormattedDate(
        date as string | undefined,
        dateFormatType.SHORT
      );
    } else if (
      !!(value as FilterValue)?.unit &&
      !!(value as FilterValue)?.value
    ) {
      label = `${(value as FilterValue)?.value} ${(value as FilterValue).unit}`;
    }
  } else if (condition?.value === conditionsEnum.RECENT) {
    // for backward competability (saved filters) - need to support string value.
    // Runtime { value } can be a number (e.g. saved filters), which FilterValue's
    // string-only typing doesn't capture, so read it as string | number here.
    const recentValue = (value as FilterValue)?.value as
      | string
      | number
      | undefined;
    label =
      recentValue === 1 || value === "1"
        ? `Today`
        : `Last ${typeof value === "string" ? value : recentValue ? recentValue : ""} Days`;
  } else if (condition?.value === conditionsEnum.WITHIN) {
    const withinValue = (value as FilterValue)?.value as
      | string
      | number
      | undefined;
    label =
      withinValue === 1 || value === "1"
        ? `1 Day`
        : `${typeof value === "string" ? value : withinValue ? withinValue : ""} Days`;
  } else if (isSetGroup(group)) {
    label =
      (currentValueObj?.length ?? 0) > MAX_VALUES_LENGTH && !isTitle
        ? `${currentValueObj?.length} selected`
        : currentValueObj?.map((option) => option.label)?.join(", ");
  } else if (Array.isArray(value)) {
    const formattedValues = fieldsToConvertToBeautifulString?.includes(
      splitFilterKey(field).property
    )
      ? value?.map((item) => convertToBeautifulString(item as string))
      : value;
    label =
      value?.length > MAX_VALUES_LENGTH && !isTitle
        ? `${value?.length} selected`
        : (formattedValues as string[])?.join(", ");
  } else {
    label = value;
  }

  if (ageFields?.includes(splitFilterKey(field).property) && !!value) {
    label = `${label} Days`;
  }

  // Safeguard: if the fallthrough branch produced a non-renderable {value, unit}
  // object (e.g. when condition is undefined because the field is missing from
  // fieldsData on a dashboard), coerce it to a formatted string so React can
  // render it without throwing "Objects are not valid as a React child".
  if (
    label &&
    typeof label === "object" &&
    "value" in label &&
    "unit" in label
  ) {
    label = `${(label as FilterValue).value} ${(label as FilterValue).unit}`;
  }

  return label as ReactNode;
};

export { getFieldLabel };

const toConditionOption = (
  condition: string,
  label: string | undefined,
  longLabel: string | undefined = conditionLabels[condition]?.long
): ConditionOption => ({
  label,
  longLabel,
  value: condition,
  type: Object.hasOwn(conditionTypes, condition)
    ? conditionTypes[condition]
    : undefined,
  deprecated: deprecatedConditions.includes(condition),
});

const getUnknownFieldConditionOption = (condition: string): ConditionOption =>
  toConditionOption(
    condition,
    conditionLabels[condition]?.short ?? condition,
    conditionLabels[condition]?.long ?? condition
  );

const useFilterDetails = (
  fieldsData: FieldData[] | undefined,
  filterField: string | null | undefined,
  currentCondition: string | null | undefined,
  currentValue: FilterValueLike,
  statuses: unknown[],
  filtersToPin: FilterPinItem[] = []
): UseFilterDetailsReturn => {
  const { getSeverityField } = useSeverityAsPriority();
  const { logError } = useLogger();
  const filterProperty = splitFilterKey(filterField).property;
  const { getAnalystDisplayName } = useAnalystInstanceNames(
    filterProperty === filterFieldsEnum.ANALYST_INSTANCE_ID
  );
  // `currentTheme` is read off the context as it was before the TS migration.
  // It is not declared on GeneralAppContextType (it lived on the untyped JS
  // context value), so widen the read locally without changing the access path.
  const { currentTheme } = useContext(
    GeneralAppContext
  ) as GeneralAppContextType & {
    currentTheme?: string;
  };

  const currentField = useMemo(
    () =>
      fieldsData?.find((field) =>
        isSameRemediationItemFilterKey(field.field, filterField)
      ),
    [fieldsData, filterField]
  );

  const isUnknownField = !currentField && !!fieldsData?.length;

  const fieldType = useMemo(
    () => currentField?.type?.toLowerCase(),
    [currentField]
  );

  const fieldConditions = useMemo(
    () => currentField?.conditions || [],
    [currentField]
  );

  const isDateField = useMemo(
    () => getIsDateField(fieldType, fieldConditions),
    [fieldType, fieldConditions]
  );

  const fieldData = useMemo<
    Partial<FieldData> & { label?: string; pinned?: boolean }
  >(() => {
    const filterToPin = filtersToPin?.find((filter) =>
      isSameRemediationItemFilterKey(filter.filterField, filterField)
    );

    if (filterToPin) {
      return {
        ...currentField,
        label: filterToPin?.label,
        pinned: true,
      };
    }

    return {
      ...currentField,
      label: isUnknownField
        ? (getKnownAdditionalDataTitle(filterProperty) ?? filterProperty)
        : (currentField?.label ?? getAdditionalDataTitle(filterProperty)),
    };
  }, [currentField, isUnknownField, filtersToPin, filterField, filterProperty]);

  const conditionOptions = useMemo<ConditionOption[]>(() => {
    if (
      isActualStatusKey(filterField) ||
      readOnlyFields?.includes(filterProperty)
    ) {
      return [
        {
          label: "=",
          longLabel: "Equals (=)",
          value: "in",
          type: "EQ",
        },
      ];
    }

    if (isUnknownField) {
      return currentCondition
        ? [getUnknownFieldConditionOption(currentCondition)]
        : [];
    }

    return (
      currentField?.conditions
        ?.filter((conditionOption) => conditionOption !== conditionsEnum.RANGE)
        .map((conditionOption) =>
          toConditionOption(
            conditionOption,
            isDateField && !ageFields.includes(filterProperty)
              ? conditionDateLabel[conditionOption]
              : conditionLabels[conditionOption]?.short
          )
        )
        ?.sort((a, b) => {
          if (a.deprecated === b.deprecated) {
            return 0;
          }
          return a.deprecated ? 1 : -1;
        }) ?? []
    );
  }, [
    currentField,
    isUnknownField,
    currentCondition,
    isDateField,
    filterField,
    filterProperty,
  ]);

  const conditionData = useMemo(() => {
    return conditionOptions?.find(
      (option) => option.value === currentCondition
    );
  }, [conditionOptions, currentCondition, filterField]);

  const selectedValueData = useMemo<SelectedValueData>(() => {
    let selectedOption: FieldValueOption[] = [];

    if (isActualStatusKey(filterField)) {
      // `statuses` arrives untyped from callers (as it did in main's JS); read
      // it through getActualStatusNodes' own treeData param type so the call
      // stays well-typed without importing the non-exported TreeNode shape.
      const selectedNodes = getActualStatusNodes(
        currentValue,
        statuses as Parameters<typeof getActualStatusNodes>[1]
      );
      selectedOption = selectedNodes?.map((status) => ({ label: status.name }));
    } else if (
      (!!conditionData &&
        isSetGroup(getConditionGroup(conditionData?.value ?? ""))) ||
      fieldData?.display_options
    ) {
      selectedOption = Array.isArray(currentValue)
        ? ((getFieldValueOptions(
            filterField ?? undefined,
            currentValue,
            currentTheme
          ) ?? []) as FieldValueOption[])
        : [];
      if (
        filterProperty === filterFieldsEnum.SEVERITY ||
        filterProperty === filterFieldsEnum.AI_ANALYST_PRIORITY
      ) {
        selectedOption = selectedOption?.map((option) => {
          const rawValue = (option?.value as Array<string | number>)?.[0];
          const severityName = getSeverityField(
            rawValue,
            SeverityFieldsEnum.NAME
          );
          if (!severityName) {
            const message = `Unmapped ${filterField} value "${rawValue}" in filter details`;
            logError(new Error(message), message, {
              severity: LOG_SEVERITY.CRITICAL,
              tags: { feature: "filter_details", field: String(filterField) },
              extra: { value: rawValue },
            });
          }
          return { ...option, label: capitalize(severityName || "Unknown") };
        });
      }
      if (filterProperty === filterFieldsEnum.ANALYST_INSTANCE_ID) {
        selectedOption = selectedOption?.map((option) => ({
          ...option,
          label: getAnalystDisplayName(
            Array.isArray(option.value) ? option.value[0] : option.value
          ),
        }));
      }
    }

    return {
      selectedOptions: selectedOption,
      label: getFieldLabel(
        currentValue,
        selectedOption,
        filterField,
        conditionData,
        false,
        isDateField
      ),
      title: getFieldLabel(
        currentValue,
        selectedOption,
        filterField,
        conditionData,
        true,
        isDateField
      ),
    };
  }, [
    conditionData,
    filterField,
    filterProperty,
    currentValue,
    statuses,
    isDateField,
    getAnalystDisplayName,
  ]);

  return {
    fieldData,
    conditionData,
    selectedValueData,
    conditionOptions,
  };
};

export default useFilterDetails;

export const getFilterConditionByField = (
  field: string | null | undefined,
  fieldsData: FieldData[] | undefined
): string | undefined => {
  if (!field) {
    return undefined;
  }

  const currentField = fieldsData?.find((selectedField) =>
    isSameRemediationItemFilterKey(selectedField.field, field)
  );
  const conditions = currentField?.conditions;
  const fieldType = currentField?.type?.toLowerCase();

  const isDateField = getIsDateField(fieldType, conditions);

  const hasCompareCondition = conditions?.some(
    (condition) => getConditionGroup(condition) === conditionGroupsEnum.COMPARE
  );

  let selectedCondition = conditions?.filter(
    (c) => !deprecatedConditions.includes(c)
  )?.[0];

  if (
    !isDateField &&
    !ageFields.includes(splitFilterKey(field).property) &&
    hasCompareCondition
  ) {
    selectedCondition = conditionsEnum.GTE;
  } else if (conditions?.includes(conditionsEnum.OVERLAPEXACTMATCH)) {
    selectedCondition = conditionsEnum.OVERLAPEXACTMATCH;
  } else if (conditions?.includes(conditionsEnum.IN)) {
    selectedCondition = conditionsEnum.IN;
  }

  return selectedCondition;
};
